import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Autocomplete,
  Tooltip,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Popover,
  Snackbar,
  Alert
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SettingsIcon from '@mui/icons-material/Settings';
import AddIcon from '@mui/icons-material/Add';
import { TABLE_CONTAINER_STYLE, NUMERICAL_COLUMN_WIDTH, AMOUNT_COLUMN_WIDTH } from '../../constants/invoiceConstants';
import { useTheme } from '@mui/material/styles';
import { formatNumber, debounce } from '../../utils/invoiceCalculations';

/**
 * Purchase Return Items Table Component
 * 
 * @param {Object} props
 * @param {Array} props.items - Array of Purchase Return items
 * @param {Function} props.onItemChange - Function to handle item changes
 * @param {Function} props.onRemoveItem - Function to remove an item
 * @param {Object} props.visibleColumns - Object containing visibility state of columns
 * @param {Array} props.products - Array of available products
 * @param {Function} props.onSettingsClick - Function to handle settings button click
 * @param {Boolean} props.readOnly - If true, only quantity field is editable
 */
const PurchaseReturnItemsTable = ({
  items,
  onItemChange,
  onRemoveItem,
  visibleColumns,
  products,
  onSettingsClick,
  readOnly = false
}) => {
  // State for settings popover
  const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);
  const settingsOpen = Boolean(settingsAnchorEl);

  // State for error message
  const [errorMessage, setErrorMessage] = useState('');
  const [showError, setShowError] = useState(false);
  
  // State to store original quantities
  const [originalQuantities, setOriginalQuantities] = useState({});

  // Initialize original quantities when items change
  useEffect(() => {
    const newOriginalQuantities = {};
    items.forEach(item => {
      if (!originalQuantities[item.id]) {
        newOriginalQuantities[item.id] = item.quantity;
      }
    });
    setOriginalQuantities(prevState => ({
      ...prevState,
      ...newOriginalQuantities
    }));
  }, [items]);

  // Calculate totals for an item
  const calculateItemTotals = (item) => {
    const netValue = item.quantity * item.price;
    const discountAmount = (netValue * (item.discount || 0)) / 100;
    const netValueAfterDiscount = netValue - discountAmount;
    const vatAmount = (netValueAfterDiscount * item.vatRate) / 100;
    const total = netValueAfterDiscount + vatAmount;

    return {
      netValue,
      discountAmount,
      netValueAfterDiscount,
      vatAmount,
      total
    };
  };
  
  // Calculate totals for the summary row
  const calculateTotals = () => {
    return items.reduce((totals, item) => {
      const itemTotals = calculateItemTotals(item);
      totals.quantity += item.quantity || 0;
      totals.lineTotal += item.quantity * item.price || 0;
      totals.discountAmount += itemTotals.discountAmount || 0;
      totals.netValue += itemTotals.netValueAfterDiscount || 0;
      totals.vatAmount += itemTotals.vatAmount || 0;
      totals.grandTotal += itemTotals.total || 0;
      return totals;
    }, {
      quantity: 0,
      lineTotal: 0,
      discountAmount: 0,
      netValue: 0,
      vatAmount: 0,
      grandTotal: 0
    });
  };

  const totals = calculateTotals();
  
  // Handle settings icon click
  const handleSettingsClick = (event) => {
    setSettingsAnchorEl(event.currentTarget);
    if (onSettingsClick) {
      onSettingsClick(event);
    }
  };

  // Handle settings popover close
  const handleSettingsClose = () => {
    setSettingsAnchorEl(null);
  };
  
  // Handle focus on input fields
  const handleFocus = (event) => {
    event.target.select();
  };

  // Handle column toggle
  const handleColumnToggle = (column) => {
    if (onSettingsClick) {
      // Call parent's onSettingsClick with column information
      const event = { target: { name: column, value: !visibleColumns[column] } };
      onSettingsClick(event);
    }
  };

  return (
    <>
      <TableContainer 
        component={Paper} 
        elevation={0} 
        sx={{
          ...TABLE_CONTAINER_STYLE,
          overflow: 'auto',
          maxHeight: '60vh',
          marginBottom: '24px',
          '& .MuiTableCell-root': {
            whiteSpace: 'nowrap',
            padding: '4px 8px',
            fontSize: '0.875rem',
            fontWeight: 500,
            color: 'text.primary',
            height: '36px',
          },
          '& .MuiTableCell-head': {
            backgroundColor: (theme) => theme.palette.background.default,
            fontWeight: 600,
            position: 'sticky',
            top: 0,
            zIndex: 1,
          },
          '& .MuiTableRow-root:hover': {
            backgroundColor: (theme) => theme.palette.action.hover,
          },
        }}
        
      >
        <Table
          size="small"
          sx={{
            width: '100%',
            '& .MuiTableCell-root': {
              borderBottom: '1px solid',
              borderColor: 'divider',
            },
          }}
        >
          <TableHead>
          <TableRow>
            <TableCell
              width="100%"
              align="left"
              sx={{
                fontWeight: 600,
                padding: '4px 8px',
              }}
            >
              Item
            </TableCell>
            <TableCell align="center" width={NUMERICAL_COLUMN_WIDTH}>Qty</TableCell>
            <TableCell align="center" width={AMOUNT_COLUMN_WIDTH}>Unit Price</TableCell>
            {visibleColumns.priceWithVat && <TableCell align="center" width={AMOUNT_COLUMN_WIDTH}>Price + VAT</TableCell>}
            <TableCell align="center" width={AMOUNT_COLUMN_WIDTH}>Total Amt</TableCell>
            {visibleColumns.discountPerc && <TableCell align="center" width={NUMERICAL_COLUMN_WIDTH}>Discount %</TableCell>}
            {visibleColumns.discountAmt && <TableCell align="center" width={AMOUNT_COLUMN_WIDTH}>Discount Amt</TableCell>}
            {visibleColumns.netValue && <TableCell align="center" width={AMOUNT_COLUMN_WIDTH}>Net Value</TableCell>}
            <TableCell align="center" width={NUMERICAL_COLUMN_WIDTH}>VAT %</TableCell>
            {visibleColumns.vatAmount && <TableCell align="center" width={AMOUNT_COLUMN_WIDTH}>VAT Amt</TableCell>}
            {visibleColumns.grandTotal && <TableCell align="center" width={AMOUNT_COLUMN_WIDTH}>Grand Total</TableCell>}
            {visibleColumns.description && <TableCell align="center">Description</TableCell>}
            <TableCell align="center" width="50px">
              <Tooltip title="Column Settings">
                <IconButton 
                  size="small" 
                  onClick={handleSettingsClick}
                  sx={{ p: 0.5 }}
                >
                  <SettingsIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item) => {
            const totals = calculateItemTotals(item);
            return (
              <TableRow key={item.id}>
                <TableCell align="left">
                  <Autocomplete
                    options={products}
                    getOptionLabel={(option) => option.name || ''}
                    value={item.product}
                    onChange={(event, newValue) => onItemChange(item.id, 'product', newValue)}
                    disabled={readOnly}
                    size="small"
                    sx={{
                      width: '100%',
                      '& .MuiOutlinedInput-root': {
                        border: 'none',
                        borderRadius: 0,
                        height: '32px',
                        width: '100%',
                        '& fieldset': {
                          border: 'none'
                        }
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        variant="standard"
                        InputProps={{
                          ...params.InputProps,
                          disableUnderline: true
                        }}
                        sx={{
                          width: '100%',
                          '& .MuiInputBase-input': {
                            color: 'text.primary',
                            fontWeight: 500,
                            padding: '4px 8px !important'
                          }
                        }}
                      />
                    )}
                  />
                </TableCell>
                <TableCell align="center">
                  <TextField
                    value={item.quantity}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Allow empty string, otherwise parse as integer
                      const newQty = value === '' ? '' : (parseInt(value) || 0);
                      console.log('onChange - New Quantity:', newQty);
                      onItemChange(item.id, 'quantity', newQty);
                    }}
                    onBlur={(e) => {
                      const value = e.target.value;
                      // Skip validation if field is empty
                      if (value === '') return;
                      
                      const currentQty = parseInt(value) || 0;
                      const originalQty = originalQuantities[item.id] || 0;
                      
                      console.log('onBlur - Debug Info:', {
                        currentQty,
                        originalQty,
                        storedOriginalQty: originalQuantities[item.id],
                        item: item
                      });
                      
                      // Show error if current quantity exceeds original quantity
                      if (currentQty > originalQty) {
                        console.log('Validation Failed - Showing Error');
                        setErrorMessage(`Quantity ${currentQty} exceeds reference invoice quantity ${originalQty}`);
                        setShowError(true);
                        // Reset to original value
                        onItemChange(item.id, 'quantity', originalQty);
                        // Focus back on the field
                        e.target.focus();
                      } else {
                        console.log('Validation Passed');
                      }
                    }}
                    type="number"
                    size="small"
                    variant="standard"
                    InputProps={{ 
                      disableUnderline: true,
                      inputProps: { 
                        min: 0,
                        style: { textAlign: 'center' }
                      }
                    }}
                    sx={{
                      width: NUMERICAL_COLUMN_WIDTH,
                      '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                        display: 'none',
                      },
                      '& input': {
                        MozAppearance: 'textfield',
                        color: 'text.primary',
                        fontWeight: 500
                      },
                      '& .MuiInputBase-root': {
                        border: '1px solid rgba(0, 0, 0, 0.23)',
                        borderRadius: '4px',
                        paddingX: '4px',
                        transition: 'border-color 0.2s ease',
                      },
                      '& .MuiInputBase-root:hover': {
                        borderColor: '#1976d2',
                      },
                      '& .MuiInputBase-root.Mui-focused': {
                        borderColor: '#1976d2',
                        backgroundColor: '#e3f2fd',
                      },
                    }}
                    onFocus={(e) => e.target.select()}
                  />
                </TableCell>
                <TableCell align="center">
                  <TextField
                    value={formatNumber(item.price)}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      if (newValue === '' || !isNaN(parseFloat(newValue))) {
                        onItemChange(item.id, 'price', parseFloat(newValue) || 0);
                      }
                    }}
                    type="text"
                    size="small"
                    variant="standard"
                    InputProps={{ 
                      disableUnderline: true,
                      inputProps: { 
                        min: 0, 
                        step: 0.01,
                        style: { 
                          textAlign: 'center',
                          color: 'rgba(0, 0, 0, 0.87)',
                          opacity: 1,
                          '-webkit-text-fill-color': 'rgba(0, 0, 0, 0.87)'
                        }
                      }
                    }}
                    sx={{
                      width: AMOUNT_COLUMN_WIDTH,
                      '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                        display: 'none',
                      },
                      '& input': {
                        MozAppearance: 'textfield',
                        color: 'text.primary',
                        fontWeight: 500
                      },
                      '& .MuiInputBase-root': {
                        border: '1px solid transparent',
                        borderRadius: '4px',
                        paddingX: '4px',
                        transition: 'border-color 0.2s ease',
                      },
                      '& .MuiInputBase-root.Mui-focused': {
                        borderColor: '#1976d2',
                        backgroundColor: '#e3f2fd',
                      },
                    }}
                    disabled={readOnly}
                    onFocus={handleFocus}
                  />
                </TableCell>
                {visibleColumns.priceWithVat && (
                  <TableCell align="center">
                    <TextField
                      value={formatNumber(item.price * (1 + (item.vatRate || 15) / 100))}
                      type="text"
                      size="small"
                      variant="standard"
                      InputProps={{
                        readOnly: true,
                        disableUnderline: true,
                        inputProps: {
                          style: { textAlign: 'center' }
                        }
                      }}
                      sx={{
                        width: AMOUNT_COLUMN_WIDTH,
                        '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                          display: 'none',
                        },
                        '& input': {
                          MozAppearance: 'textfield',
                        }
                      }}
                    />
                  </TableCell>
                )}
                <TableCell align="center">
                  <TextField
                    value={formatNumber(totals.netValue)}
                    type="text"
                    size="small"
                    variant="standard"
                    InputProps={{
                      readOnly: true,
                      disableUnderline: true,
                      inputProps: {
                        style: { textAlign: 'center' }
                      }
                    }}
                    sx={{
                      width: AMOUNT_COLUMN_WIDTH,
                      '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                        display: 'none',
                      },
                      '& input': {
                        MozAppearance: 'textfield',
                      }
                    }}
                  />
                </TableCell>
                {visibleColumns.discountPerc && (
                  <TableCell align="center">
                    <TextField
                      value={formatNumber(item.discount)}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        if (newValue === '' || !isNaN(parseFloat(newValue))) {
                          onItemChange(item.id, 'discount', parseFloat(newValue) || 0);
                        }
                      }}
                      type="text"
                      size="small"
                      variant="standard"
                      InputProps={{ 
                        disableUnderline: true,
                        inputProps: { 
                          min: 0, 
                          max: 100,
                          style: { textAlign: 'center' }
                        }
                      }}
                      sx={{ 
                        width: NUMERICAL_COLUMN_WIDTH,
                        '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                          display: 'none',
                        },
                        '& input': {
                          MozAppearance: 'textfield',
                        },
                        '& .MuiInputBase-root': {
                          border: '1px solid rgba(0, 0, 0, 0.23)',
                          borderRadius: '4px',
                          paddingX: '4px',
                          transition: 'border-color 0.2s ease',
                        },
                        '& .MuiInputBase-root:hover': {
                          borderColor: '#1976d2',
                        },
                        '& .MuiInputBase-root.Mui-focused': {
                          borderColor: '#1976d2',
                          backgroundColor: '#e3f2fd',
                        },
                      }}
                      disabled={readOnly}
                      onFocus={handleFocus}
                    />
                  </TableCell>
                )}
                {visibleColumns.discountAmt && (
                  <TableCell align="center">
                    <TextField
                      value={formatNumber(totals.discountAmount)}
                      type="text"
                      size="small"
                      variant="standard"
                      InputProps={{
                        readOnly: true,
                        disableUnderline: true,
                        inputProps: {
                          style: { textAlign: 'center' }
                        }
                      }}
                      sx={{
                        width: AMOUNT_COLUMN_WIDTH,
                        '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                          display: 'none',
                        },
                        '& input': {
                          MozAppearance: 'textfield',
                        }
                      }}
                    />
                  </TableCell>
                )}
                {visibleColumns.netValue && (
                  <TableCell align="center">
                    <TextField
                      value={formatNumber(totals.netValueAfterDiscount)}
                      type="text"
                      size="small"
                      variant="standard"
                      InputProps={{
                        readOnly: true,
                        disableUnderline: true,
                        inputProps: {
                          style: { textAlign: 'center' }
                        }
                      }}
                      sx={{
                        width: AMOUNT_COLUMN_WIDTH,
                        '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                          display: 'none',
                        },
                        '& input': {
                          MozAppearance: 'textfield',
                        }
                      }}
                    />
                  </TableCell>
                )}
                <TableCell align="center">
                  <TextField
                    value={formatNumber(item.vatRate)}
                    type="text"
                    size="small"
                    variant="standard"
                    InputProps={{
                      readOnly: true,
                      disableUnderline: true,
                      inputProps: {
                        style: { textAlign: 'center' }
                      }
                    }}
                    sx={{
                      width: NUMERICAL_COLUMN_WIDTH,
                      '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                        display: 'none',
                      },
                      '& input': {
                        MozAppearance: 'textfield',
                      }
                    }}
                  />
                </TableCell>
                {visibleColumns.vatAmount && (
                  <TableCell align="center">
                    <TextField
                      value={formatNumber(totals.vatAmount)}
                      type="text"
                      size="small"
                      variant="standard"
                      InputProps={{
                        readOnly: true,
                        disableUnderline: true,
                        inputProps: {
                          style: { textAlign: 'center' }
                        }
                      }}
                      sx={{
                        width: AMOUNT_COLUMN_WIDTH,
                        '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                          display: 'none',
                        },
                        '& input': {
                          MozAppearance: 'textfield',
                        }
                      }}
                    />
                  </TableCell>
                )}
                {visibleColumns.grandTotal && (
                  <TableCell align="center">
                    <TextField
                      value={formatNumber(totals.total)}
                      type="text"
                      size="small"
                      variant="standard"
                      InputProps={{
                        readOnly: true,
                        disableUnderline: true,
                        inputProps: {
                          style: { textAlign: 'center' }
                        }
                      }}
                      sx={{
                        width: AMOUNT_COLUMN_WIDTH,
                        '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                          display: 'none',
                        },
                        '& input': {
                          MozAppearance: 'textfield',
                        }
                      }}
                    />
                  </TableCell>
                )}
                {visibleColumns.description && (
                  <TableCell align="center">
                    <TextField
                      value={item.description}
                      onChange={(e) => onItemChange(item.id, 'description', e.target.value)}
                      size="small"
                      variant="standard"
                      fullWidth
                      InputProps={{ disableUnderline: true }}
                      sx={{
                        '& .MuiInputBase-root': {
                          border: '1px solid rgba(0, 0, 0, 0.23)',
                          borderRadius: '4px',
                          padding: '0 4px',
                          transition: 'border-color 0.2s ease',
                        },
                        '& .MuiInputBase-root:hover': {
                          borderColor: '#1976d2',
                        },
                        '& .MuiInputBase-root.Mui-focused': {
                          borderColor: '#1976d2',
                          backgroundColor: '#e3f2fd',
                        },
                      }}
                      disabled={readOnly}
                    />
                  </TableCell>
                )}
                <TableCell align="center">
                  <IconButton 
                    size="small" 
                    onClick={() => onRemoveItem(item.id)} 
                    sx={{ p: 0.5 }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
          
          {/* Totals Row */}
          <TableRow sx={{ 
            position: 'sticky', 
            bottom: 0, 
            bgcolor: '#f1f4fd',
            borderTop: '2px solid rgba(0, 0, 0, 0.12)',
            '& td': {
              fontWeight: 'bold',
              fontSize: '0.875rem',
              borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
            }
          }}>
            <TableCell align="left">Line Totals</TableCell>
            <TableCell align="center">
              {formatNumber(totals.quantity)}
            </TableCell>
            <TableCell align="center">
              {items.length > 0 ? formatNumber(items.reduce((sum, item) => sum + (Number(item.price) || 0), 0) / items.length) : '0.00'}
            </TableCell>
            {visibleColumns.priceWithVat && (
              <TableCell align="center">
                {items.length > 0 ? formatNumber(items.reduce((sum, item) => sum + ((Number(item.price) || 0) * (1 + (Number(item.vatRate) || 0) / 100)), 0) / items.length) : '0.00'}
              </TableCell>
            )}
            <TableCell align="center">
              {formatNumber(totals.lineTotal)}
            </TableCell>
            {visibleColumns.discountPerc && (
              <TableCell align="center">
                {formatNumber((items.reduce((sum, item) => sum + (Number(item.discount) || 0), 0) / 
                  Math.max(items.length, 1)))}
              </TableCell>
            )}
            {visibleColumns.discountAmt && (
              <TableCell align="center">
                {formatNumber(totals.discountAmount)}
              </TableCell>
            )}
            {visibleColumns.netValue && (
              <TableCell align="center">
                {formatNumber(totals.netValue)}
              </TableCell>
            )}
            <TableCell align="center">
              {formatNumber((items.reduce((sum, item) => sum + (Number(item.vatRate) || 0), 0) / 
                Math.max(items.length, 1)))}
            </TableCell>
            {visibleColumns.vatAmount && (
              <TableCell align="center">
                {formatNumber(totals.vatAmount)}
              </TableCell>
            )}
            {visibleColumns.grandTotal && (
              <TableCell align="center">
                {formatNumber(totals.grandTotal)}
              </TableCell>
            )}
            {visibleColumns.description && <TableCell />}
            <TableCell />
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>

    {/* Column Settings Popover */}
    <Popover
      open={settingsOpen}
      anchorEl={settingsAnchorEl}
      onClose={handleSettingsClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
    >
      <List>
        {Object.entries(visibleColumns).map(([column, visible]) => (
          <ListItem key={column} button onClick={() => handleColumnToggle(column)}>
            <ListItemIcon>
              <Checkbox
                edge="start"
                checked={visible}
                tabIndex={-1}
                disableRipple
              />
            </ListItemIcon>
            <ListItemText primary={column.charAt(0).toUpperCase() + column.slice(1).replace(/([A-Z])/g, ' $1')} />
          </ListItem>
        ))}
      </List>
    </Popover>

    {/* Error Message Snackbar */}
    <Snackbar
      open={showError}
      autoHideDuration={3000}
      onClose={() => setShowError(false)}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Alert
        onClose={() => setShowError(false)}
        severity="error"
        variant="filled"
        sx={{ width: '100%' }}
      >
        {errorMessage}
      </Alert>
    </Snackbar>
    </>
  );
};

export default PurchaseReturnItemsTable;
